// ✅ src/pages/VehicleV2/EquipmentEntry.jsx

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { FaArrowLeft, FaSave } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/equipment";

const onlyAllowed = (s) => /^[a-zA-Z0-9 ]*$/.test(s || "");
const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const EquipmentEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // ✅ same localStorage style as listing page
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginUserId") ||
    localStorage.getItem("loginuserid") ||
    sessionStorage.getItem("loginUserId") ||
    sessionStorage.getItem("loginuserid") ||
    "";

  // ✅ edit id from session
  const [equipmentId, setEquipmentId] = useState(
    () => sessionStorage.getItem("EquipID") || ""
  );

  const isEdit = useMemo(
    () => String(equipmentId || "").trim() !== "",
    [equipmentId]
  );

  const [equipmentType, setEquipmentType] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  // ✅ same style as listing
  const branchActive = String(
    localStorage.getItem("BranchActive") ||
      sessionStorage.getItem("BranchActive") ||
      "1"
  ) === "1";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const goBack = () => {
    sessionStorage.removeItem("EquipID");
    navigate("/vehicle/EquipmentListing");
  };

  const fetchEquipment = async (id) => {
    if (!id) return;

    setLoading(true);
    setMsg("");

    try {
      const r = await axios.get(`${API_BASE}/view`, {
        params: { equipmentId: id },
        headers: commonHeaders,
      });

      if (r.data?.success === false) {
        throw new Error(r.data?.message || "Failed to load equipment");
      }

      const row = r.data?.row || r.data?.data || null;

      if (!row) {
        toast.warn("Record not found");
        sessionStorage.removeItem("EquipID");
        setEquipmentId("");
        setEquipmentType("");
        return;
      }

      setEquipmentType(
        String(
          row?.fld_equipmenttype ||
            row?.equipmentType ||
            row?.EquipmentType ||
            ""
        )
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load equipment"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchEquipment(equipmentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, equipmentId, commonHeaders]);

  const validate = () => {
    const v = String(equipmentType || "");

    if (v.trim() === "") {
      alert("Please give input in 'Equipment type' field");
      setEquipmentType("");
      return false;
    }

    if (v.replace(/\s/g, "").length === 0) {
      alert("Please Enter Equipment Name");
      setEquipmentType("");
      return false;
    }

    if (!onlyAllowed(v)) {
      alert("Only letters, numbers and spaces are allowed");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setMsg("");

    if (!validate()) return;

    try {
      setLoading(true);

      const payload = {
        equipmentId: isEdit ? toInt(equipmentId) : 0,
        equipmentType: equipmentType.trim(),
        loginUserId: toInt(userId),
      };

      const r = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (r.data?.success === false) {
        const m = r.data?.message || "Save failed";
        setMsg(m);
        toast.warn(m);
        return;
      }

      const successMsg =
        r.data?.message ||
        (isEdit ? "Updated Successfully" : "Inserted Successfully");

      toast.success(successMsg);

      sessionStorage.removeItem("EquipID");
      navigate("/vehicle/EquipmentListing");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Save failed"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">EQUIPMENT ENTRY</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <div>
            <h2
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              {isEdit ? "Edit Equipment" : "Add Equipment"}
            </h2>

            <div
              className={`text-sm mt-1 ${
                isDark ? "text-gray-300" : "text-gray-600"
              }`}
            >
              INFRA SCREEN : B-5/1
            </div>
          </div>

          <button
            onClick={goBack}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? "border-gray-600 text-white hover:bg-gray-800"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FaArrowLeft />
            Back
          </button>
        </div>

        {/* Form */}
        <div
          className={`rounded-xl p-5 border ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-1">
              <label className={labelClass}>
                Equipment Type <span className="text-red-500">*</span>
              </label>

              <input
                type="text"
                value={equipmentType}
                maxLength={50}
                onChange={(e) => {
                  const next = e.target.value;
                  if (onlyAllowed(next)) setEquipmentType(next);
                }}
                className={inputClass}
                placeholder="Enter equipment type..."
                disabled={loading}
                autoFocus
              />
            </div>
          </div>

          {msg ? (
            <div className="mt-5">
              <div className="text-red-500 font-medium">{msg}</div>
            </div>
          ) : null}

          <div className="mt-8 flex items-center gap-3">
            {branchActive ? (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  loading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <FaSave />
                {loading ? "Saving..." : isEdit ? "Update" : "Submit"}
              </button>
            ) : (
              <div
                className={`text-sm ${
                  isDark ? "text-gray-300" : "text-gray-600"
                }`}
              >
                Submit disabled (Branch not active)
              </div>
            )}
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default EquipmentEntry;